'use client';

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import { RefreshCw } from 'lucide-react';
import { haptics, isTouch } from '@/lib/native';

export const PULL_REFRESH_EVENT = 'sweetspot:refresh';

const THRESHOLD = 72; // px of pull that triggers a refresh

/** True when `main` is the page's scroller (browse pages), false on full-height pages that clip it. */
function ownsScroll(el: HTMLElement): boolean {
  const oy = getComputedStyle(el).overflowY;
  return oy === 'auto' || oy === 'scroll';
}

/** A touch that starts inside a nested vertical scroller (chat list, side panel) belongs to that scroller. */
function insideOtherScroller(target: Element | null, main: HTMLElement): boolean {
  let node: Element | null = target;
  while (node && node !== main) {
    const oy = getComputedStyle(node).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && node.scrollHeight > node.clientHeight) return true;
    node = node.parentElement;
  }
  return false;
}
const MAX_PULL = 110;
const RESISTANCE = 0.55;

interface PullToRefreshProps {
  children: ReactNode;
  className?: string;
  /** Storefront pages already own their semantic `<main>`. */
  as?: 'main' | 'div';
  /** Extra work to run on refresh (data fetches). router.refresh() always runs. */
  onRefresh?: () => Promise<unknown> | void;
}

/**
 * iOS-style pull to refresh on a scroll container. Only arms when the
 * container is scrolled to the very top and the gesture is a downward drag;
 * horizontal swipes and inner scrolls pass through untouched. Dispatches
 * `sweetspot:refresh` (pages listen and refetch) and calls router.refresh().
 * Touch devices only — desktop gets the plain container.
 */
export function PullToRefresh({ children, className, as = 'main', onRefresh }: PullToRefreshProps) {
  const router = useRouter();
  const ref = useRef<HTMLElement>(null);
  const setRef = useCallback((node: HTMLElement | null) => {
    ref.current = node;
  }, []);
  const startY = useRef<number | null>(null);
  const startX = useRef(0);
  const armed = useRef(false);
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const crossed = useRef(false);

  useEffect(() => {
    setEnabled(isTouch());
  }, []);

  const finish = useCallback(async () => {
    setRefreshing(true);
    setPull(THRESHOLD * 0.7);
    void haptics.medium();
    window.dispatchEvent(new CustomEvent(PULL_REFRESH_EVENT));
    try {
      await Promise.all([onRefresh?.(), new Promise((r) => setTimeout(r, 600))]);
      router.refresh();
    } finally {
      setRefreshing(false);
      setPull(0);
    }
  }, [onRefresh, router]);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if (!enabled || refreshing) return;
    const el = ref.current;
    // React bubbles touches from portals (sheets, dialogs) through here although they sit outside the scroller.
    const insideScroller = el?.contains(e.target as Node) ?? false;
    if (!el || !insideScroller || el.scrollTop > 0 || !ownsScroll(el) || insideOtherScroller(e.target as Element | null, el)) {
      startY.current = null;
      return;
    }
    startY.current = e.touches[0].clientY;
    startX.current = e.touches[0].clientX;
    armed.current = false;
    crossed.current = false;
  }, [enabled, refreshing]);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (startY.current == null || refreshing) return;
    const dy = e.touches[0].clientY - startY.current;
    const dx = Math.abs(e.touches[0].clientX - startX.current);
    if (!armed.current) {
      if (dy < 8) return; // not a downward pull yet
      if (dx > dy) {
        startY.current = null; // horizontal — leave it alone
        return;
      }
      armed.current = true;
    }
    const el = ref.current;
    if (!el || el.scrollTop > 0) {
      startY.current = null;
      setPull(0);
      return;
    }
    const next = Math.min(MAX_PULL, Math.max(0, dy * RESISTANCE));
    setPull(next);
    if (next >= THRESHOLD && !crossed.current) {
      crossed.current = true;
      void haptics.light();
    } else if (next < THRESHOLD && crossed.current) {
      crossed.current = false;
    }
  }, [refreshing]);

  const onTouchEnd = useCallback(() => {
    if (startY.current == null) return;
    startY.current = null;
    if (!armed.current) return;
    if (pull >= THRESHOLD) void finish();
    else setPull(0);
  }, [finish, pull]);

  const progress = Math.min(1, pull / THRESHOLD);
  const showing = pull > 0 || refreshing;

  const content = (
    <>
      {enabled && (
        <div
          className="ptr-indicator"
          style={{
            height: showing ? pull : 0,
            transition: startY.current == null ? 'height 260ms cubic-bezier(0.32,0.72,0,1)' : 'none',
          }}
          aria-hidden="true"
        >
          <div
            className="ptr-spinner"
            style={{
              opacity: Math.max(0.25, progress),
              transform: `scale(${0.6 + 0.4 * progress}) rotate(${refreshing ? 0 : progress * 270}deg)`,
              transition: startY.current == null ? 'transform 200ms ease, opacity 200ms ease' : 'none',
            }}
          >
            <RefreshCw className="h-3.5 w-3.5" strokeWidth={2.5} />
          </div>
        </div>
      )}
      <div
        style={{
          transform: showing ? `translate3d(0, ${pull}px, 0)` : undefined,
          transition: startY.current == null ? 'transform 260ms cubic-bezier(0.32,0.72,0,1)' : 'none',
        }}
        className="flex flex-1 flex-col min-h-0 min-w-0"
      >
        {children}
      </div>
    </>
  );

  const scrollProps = {
    className: `relative ${className ?? ''} ${refreshing ? 'ptr-refreshing' : ''}`,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onTouchCancel: onTouchEnd,
  };

  if (as === 'div') {
    return <div ref={setRef} {...scrollProps}>{content}</div>;
  }
  return <main ref={setRef} {...scrollProps}>{content}</main>;
}
